import { useCallback, useEffect, useState } from "react";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CalendarDays, ClipboardList, Loader2, Settings2, Users, XCircle } from "lucide-react";
import LeagueDetail from "@/components/LeagueDetail";

const STATUS_CLS = {
  open: "border-emerald-500/60 text-emerald-400",
  cancelled: "border-pb-red/60 text-pb-red",
};

export default function MyLeagues({ refreshKey }) {
  const [leagues, setLeagues] = useState(null);
  const [detail, setDetail] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = useCallback(() => api.get("/admin/leagues").then(({ data }) => setLeagues(data)).catch(() => setLeagues([])), []);
  useEffect(() => { load(); }, [load, refreshKey]);

  const cancel = async (lg) => {
    if (!window.confirm(`Cancel ${lg.name}? Registered teams will no longer see it in the directory.`)) return;
    setBusy(true);
    try { await api.delete(`/admin/leagues/${lg.id}`); toast.success(`${lg.name} cancelled`); load(); }
    catch (e) { toast.error(formatApiError(e.response?.data?.detail)); }
    finally { setBusy(false); }
  };

  if (!leagues) return <div className="mb-8"><Loader2 className="w-5 h-5 text-pb-cyan animate-spin" /></div>;
  if (leagues.length === 0) return null;
  return (
    <div className="mb-8 border border-pb-cyan/40 bg-pb-cyan/5 p-4" data-testid="my-leagues-dashboard">
      <div className="flex items-center gap-2 mb-1">
        <ClipboardList className="w-4 h-4 text-pb-cyan" />
        <h3 className="font-display font-bold text-xl uppercase">My Leagues — Director Dashboard</h3>
      </div>
      <p className="text-[11px] text-zinc-500 mb-3">Every league you run in one place — approvals, schedules and results without digging through the public directory.</p>
      <div className="space-y-2">
        {leagues.map((lg) => (
          <div key={lg.id} className="border border-pb-border/70 bg-black/40 px-4 py-3 flex flex-wrap items-center gap-x-4 gap-y-2" data-testid={`my-league-${lg.id}`}>
            <div className="flex-1 min-w-[200px]">
              <div className="flex items-center gap-2 flex-wrap">
                <button data-testid={`ml-open-${lg.id}`} onClick={() => setDetail(lg)}
                  className="font-display font-bold uppercase text-left hover:text-pb-cyan transition-colors">{lg.name}</button>
                <span className={`text-[8px] font-black uppercase tracking-widest border px-1.5 py-0.5 ${STATUS_CLS[lg.status] || "border-zinc-600 text-zinc-400"}`}
                  data-testid={`ml-status-${lg.id}`}>{lg.status}</span>
                {lg.pending > 0 && (
                  <span className="text-[8px] font-black uppercase tracking-widest border border-amber-400/70 text-amber-300 bg-amber-400/10 px-1.5 py-0.5 animate-pulse"
                    data-testid={`ml-pending-${lg.id}`}>{lg.pending} approval{lg.pending === 1 ? "" : "s"} waiting</span>
                )}
              </div>
              <div className="text-[10px] uppercase tracking-widest text-zinc-500 mt-0.5 flex flex-wrap gap-x-3 gap-y-0.5">
                <span className="flex items-center gap-1"><CalendarDays className="w-3 h-3" /> {lg.start_date} · {lg.weeks} wks</span>
                <span className="flex items-center gap-1"><Users className="w-3 h-3" /> {lg.registered} team{lg.registered === 1 ? "" : "s"}{lg.spots_left !== null ? ` · ${lg.spots_left} spots left` : ""}</span>
                <span data-testid={`ml-progress-${lg.id}`}>
                  {lg.matches_total > 0 ? `Season: ${lg.matches_locked}/${lg.matches_total} matches locked` : "Season not started"}
                </span>
              </div>
            </div>
            <div className="flex items-center gap-2 ml-auto">
              <button data-testid={`ml-manage-${lg.id}`} onClick={() => setDetail(lg)}
                className="flex items-center gap-1.5 bg-pb-cyan text-black font-black uppercase tracking-widest px-3.5 py-2 text-[10px] hover:opacity-90 active:scale-95 transition-all">
                <Settings2 className="w-3.5 h-3.5" /> Manage
              </button>
              {lg.status === "open" && (
                <button data-testid={`ml-cancel-${lg.id}`} onClick={() => cancel(lg)} disabled={busy} title="Cancel this league"
                  className="p-2 border border-pb-border text-zinc-500 hover:border-pb-red hover:text-pb-red transition-colors disabled:opacity-40">
                  <XCircle className="w-4 h-4" />
                </button>
              )}
            </div>
          </div>
        ))}
      </div>
      {detail && <LeagueDetail league={{ ...detail, can_manage: true }} onClose={() => { setDetail(null); load(); }} />}
    </div>
  );
}
